/**
 * Frontend mirror of backend's calc_proportional_trade_allocations.
 * Calculates trade-in vehicle composition based on proportional distribution.
 */
export function calcProportionalTradeAllocations({
  sellerAllocations,
  purchasePrice,
  salePrice,
  totalExpenses,
}) {
  if (!sellerAllocations || sellerAllocations.length === 0) return [];
  const totalCost = Number(purchasePrice || 0) + Number(totalExpenses || 0);
  const lucro = Number(salePrice || 0) - totalCost;

  const perPartner = sellerAllocations.map((a) => {
    const capital = (Number(a.contribution_pct || 0) / 100) * totalCost;
    const profit = (Number(a.profit_pct || 0) / 100) * lucro;
    return {
      partner_id: a.partner_id,
      contribution_pct_orig: Number(a.contribution_pct || 0),
      profit_pct_orig: Number(a.profit_pct || 0),
      total_to_receive: capital + profit,
    };
  });

  const sumTR = perPartner.reduce((s, p) => s + p.total_to_receive, 0);
  if (sumTR <= 0) {
    // Fallback: simple mirror
    return sellerAllocations.map((a) => ({
      partner_id: a.partner_id,
      contribution_pct: Number(a.contribution_pct || 0),
      profit_pct: Number(a.profit_pct || 0),
    }));
  }

  const raw = perPartner.map((p) => {
    const sharePct = (p.total_to_receive / sumTR) * 100;
    const factor = p.contribution_pct_orig > 0 ? p.profit_pct_orig / p.contribution_pct_orig : 1.0;
    const rawProfit = sharePct * factor;
    return { partner_id: p.partner_id, contribution_pct: sharePct, raw_profit: rawProfit };
  });

  const sumRaw = raw.reduce((s, r) => s + r.raw_profit, 0);
  if (sumRaw <= 0) {
    return raw.map((r) => ({
      partner_id: r.partner_id,
      contribution_pct: Math.round(r.contribution_pct * 100) / 100,
      profit_pct: Math.round(r.contribution_pct * 100) / 100,
    }));
  }

  // Normalize profit + ensure sum exactly 100 (last absorbs rounding) — 2 decimal places
  const n = raw.length;
  let contribDist = 0;
  let profitDist = 0;
  const result = [];
  for (let i = 0; i < n; i += 1) {
    const isLast = i === n - 1;
    const r = raw[i];
    const normalizedProfit = (r.raw_profit / sumRaw) * 100;
    let c, pp;
    if (isLast) {
      c = Math.round((100 - contribDist) * 100) / 100;
      pp = Math.round((100 - profitDist) * 100) / 100;
    } else {
      c = Math.round(r.contribution_pct * 100) / 100;
      pp = Math.round(normalizedProfit * 100) / 100;
      contribDist += c;
      profitDist += pp;
    }
    result.push({ partner_id: r.partner_id, contribution_pct: c, profit_pct: pp });
  }
  return result;
}
